import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import SectionRow, { SECTION_ROW_HEADING } from '../../core/SectionRow';
import Chip from '../../core/Chip';
import InlineAddSelect from '../../core/InlineAddSelect';
import { ATTRIBUTE_GROUP_ROW_LEGACY_CLASS } from './constants';
import { attributeKey, attributeLabel } from './helpers';

/**
 * One attribute group ("Hard skills", "Soft skills", …): a SectionRow whose content column
 * lists the chosen attributes as removable chips, an inline "+ Add" search to add more, and a
 * slot underneath (suggestions, hints). Add and remove are optional affordances: without
 * `onAdd` there is no add control, without `onRemove` chips have no ×.
 */
const AttributeGroupRow = ({
    title,
    description,
    meta = null,
    attributes = [],
    onRemove,
    removeTooltip,
    onAdd,
    addOptions,
    addUrl,
    addTags = true,
    addLoading = false,
    addDisabled = false,
    addId,
    divider = true,
    children,
    className,
    ...rest
}) => (
    <SectionRow
        title={title}
        description={description}
        meta={meta}
        heading={SECTION_ROW_HEADING.H2}
        divider={divider}
        className={classNames('ds-attribute-group-row', ATTRIBUTE_GROUP_ROW_LEGACY_CLASS.ROW, className)}
        labelClassName={ATTRIBUTE_GROUP_ROW_LEGACY_CLASS.LABEL}
        contentClassName={ATTRIBUTE_GROUP_ROW_LEGACY_CLASS.CONTENT}
        {...rest}
    >
        <div className={classNames('ds-attribute-group-row__chips', ATTRIBUTE_GROUP_ROW_LEGACY_CLASS.INNER, ATTRIBUTE_GROUP_ROW_LEGACY_CLASS.CHIPS)}>
            {attributes.map((attribute, index) => {
                const key = attributeKey(attribute);
                const label = attributeLabel(attribute);
                return (
                    <Chip
                        key={`${key ?? label}-${index}`}
                        className="ds-attribute-group-row__chip"
                        value={label}
                        removeTooltip={removeTooltip}
                        removeLabel={removeTooltip ? `${removeTooltip}: ${label}` : undefined}
                        onRemove={typeof onRemove === 'function' ? () => onRemove(key, attribute) : undefined}
                    />
                );
            })}
            {typeof onAdd === 'function' && (
                <InlineAddSelect
                    id={addId}
                    options={addOptions}
                    url={addUrl}
                    tags={addTags}
                    loading={addLoading}
                    disabled={addDisabled}
                    onChange={onAdd}
                    className="ds-attribute-group-row__add"
                    buttonClassName={ATTRIBUTE_GROUP_ROW_LEGACY_CLASS.ADD_BUTTON}
                />
            )}
        </div>
        {children && <div className="ds-attribute-group-row__extra">{children}</div>}
    </SectionRow>
);

AttributeGroupRow.propTypes = {
    /** Group title */
    title: PropTypes.node.isRequired,
    /** Group description */
    description: PropTypes.node,
    /** Extra label-column content (hint, checkbox) */
    meta: PropTypes.node,
    /** Chosen attributes: `{ id, value, text }` */
    attributes: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.any, value: PropTypes.any, text: PropTypes.string })),
    /** `(key, attribute)` — omit to hide the × on chips */
    onRemove: PropTypes.func,
    /** Tooltip on the chip ×; also prefixes each × button's accessible name */
    removeTooltip: PropTypes.string,
    /** `(value, { data }, close)` — omit to hide the add control */
    onAdd: PropTypes.func,
    /** Static options for the add search */
    addOptions: PropTypes.array,
    /** Remote source for the add search */
    addUrl: PropTypes.string,
    /** Allow creating new values in the add search */
    addTags: PropTypes.bool,
    /** A pick is being saved */
    addLoading: PropTypes.bool,
    /** Disable the add button */
    addDisabled: PropTypes.bool,
    /** Id for the add select */
    addId: PropTypes.string,
    /** Separator under the row */
    divider: PropTypes.bool,
    /** Content under the chips (e.g. SuggestionChips) */
    children: PropTypes.node,
    /** Extra class */
    className: PropTypes.string,
};

export { ATTRIBUTE_GROUP_ROW_LEGACY_CLASS, attributeKey, attributeLabel };
export default AttributeGroupRow;
